import { InterfaceIcon, getSvgIcon } from '@helpers/icons';
import { imageUrl } from '@helpers/image';
import { Device, until } from '@helpers/media';
import { clamp } from '@helpers/utils';
import useMedia from '@hooks/useMedia';
import { useOutsideClick } from '@hooks/useOutsideClick';
import { FakeButton, LinkButton } from '@stories/Components/Buttons/Button/Button';
import * as React from 'react';
import { AnnouncementCtas } from '../AnnouncementBar/AnnouncementBar';
import S from './Navigation.styles';
const Navigation = (props) => {
    const isDesktop = useMedia([until(Device.TabletLarge)], [false], true);
    const [focus, setFocus] = React.useState();
    const itemsRef = React.useRef([]);
    const containerRef = useOutsideClick(() => {
        props.setActiveLink(-1);
    });
    React.useEffect(() => {
        window.addEventListener('scroll', () => props.setActiveLink(-1), { passive: true });
        return () => {
            window.removeEventListener('scroll', () => props.setActiveLink(-1));
        };
    }, []);
    return (React.createElement(React.Fragment, null,
        React.createElement(S.Container, { ref: containerRef, id: "main-nav", "data-active": props.active, "aria-label": props.translations?.['global.menu.nav'] ?? '' },
            React.createElement(S.Links, { role: "menubar" }, props.links?.map((link, i) => (React.createElement("li", { key: link.id, role: "none" },
                React.createElement(S.TopLevelLink, { ref: (el) => (itemsRef.current[i] = el), id: i === 0 ? 'nav-first-link' : undefined, role: "menuitem", href: link.url, target: link.target, onKeyDown: (event) => {
                        const maxIndex = props.links.length - 1;
                        const nextIndex = i === maxIndex ? 0 : clamp(i + 1, maxIndex);
                        const prevIndex = i === 0 ? maxIndex : clamp(i - 1, maxIndex);
                        switch (event.key) {
                            case 'ArrowDown':
                            case 'Space':
                            case 'Enter':
                                if (link.children && link.children.length > 0) {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    props.setActiveLink(i);
                                    setFocus('first');
                                }
                                break;
                            case 'ArrowUp':
                                if (link.children && link.children.length > 0) {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    props.setActiveLink(i);
                                    setFocus('last');
                                }
                                break;
                            case 'ArrowRight':
                                event.preventDefault();
                                event.stopPropagation();
                                itemsRef.current[nextIndex]?.focus();
                                break;
                            case 'ArrowLeft':
                                event.preventDefault();
                                event.stopPropagation();
                                itemsRef.current[prevIndex]?.focus();
                                break;
                            case 'Home':
                                event.preventDefault();
                                event.stopPropagation();
                                itemsRef.current[0]?.focus();
                                break;
                            case 'End':
                                event.preventDefault();
                                event.stopPropagation();
                                itemsRef.current[maxIndex]?.focus();
                                break;
                            default:
                                break;
                        }
                    }, ...(link.children && link.children.length > 0
                        ? {
                            'aria-haspopup': 'menu',
                            'aria-expanded': props.activeLink === i,
                            onMouseEnter: () => {
                                if (isDesktop) {
                                    props.setActiveLink(i);
                                }
                            },
                            onFocus: () => {
                                if (isDesktop) {
                                    props.setActiveLink(i);
                                }
                            },
                            onClick: (e) => {
                                if (!isDesktop) {
                                    e.preventDefault();
                                    props.setActiveLink(i);
                                }
                            },
                        }
                        : {
                            'data-link-only': true,
                            onMouseEnter: () => props.setActiveLink(-1),
                        }) },
                    link.text,
                    link.children && link.children.length > 0 ? (React.createElement(React.Fragment, null, getSvgIcon(InterfaceIcon.Chevron))) : (React.createElement(React.Fragment, null, getSvgIcon(InterfaceIcon.ArrowRight)))),
                link.children &&
                    link.children.length > 0 &&
                    !link.children.find((l) => l.children && l.children.length > 0) ? (React.createElement(NavigationDropdown, { parent: link, links: link.children, active: props.activeLink === i, goBack: (doFocus) => {
                        props.setActiveLink(-1);
                        if (doFocus) {
                            itemsRef.current[i]?.focus();
                        }
                    }, toFocus: focus, removeFocus: () => setFocus(undefined), hasChildren: false, isDesktop: isDesktop, onMouseLeave: () => {
                        if (isDesktop) {
                            props.setActiveLink(-1);
                        }
                    } })) : null)))),
            props.links.map((link, i) => (React.createElement(React.Fragment, { key: `${link.id}-dropdown` }, link.children &&
                link.children.length > 0 &&
                link.children.find((l) => l.children && l.children.length > 0) ? (React.createElement(NavigationDropdown, { parent: link, links: link.children, active: props.activeLink === i, goBack: (doFocus) => {
                    props.setActiveLink(-1);
                    if (doFocus) {
                        itemsRef.current[i]?.focus();
                    }
                }, toFocus: focus, removeFocus: () => setFocus(undefined), hasChildren: true, isDesktop: isDesktop, onMouseLeave: () => {
                    if (isDesktop) {
                        props.setActiveLink(-1);
                    }
                } })) : null))),
            React.createElement(S.BottomContainer, null,
                props.signUpCta && (React.createElement(LinkButton, { buttonType: props.signUpCta.buttonType ? props.signUpCta.buttonType : 'primary', href: props.signUpCta.url, target: props.signUpCta.target, icon: props.signUpCta.icon }, props.signUpCta.text)),
                React.createElement(AnnouncementCtas, { ...props, regionSelector: { ...props.regionSelector, idPrefix: 'mobile-navigation' } }))),
        React.createElement(S.Background, { "aria-hidden": "true", "data-active": props.active || props.activeLink !== -1 })));
};
const NavigationDropdown = ({ onMouseLeave, ...props }) => {
    const [active, setActive] = React.useState(-1);
    const [focus, setFocus] = React.useState();
    const itemsRef = React.useRef([]);
    React.useEffect(() => {
        if (!props.active) {
            setActive(-1);
            setFocus(undefined);
            props.removeFocus();
        }
    }, [props.active]);
    React.useEffect(() => {
        setActive(-1);
    }, [props.isDesktop]);
    React.useEffect(() => {
        if (props.active && props.toFocus) {
            setTimeout(() => {
                itemsRef.current[props.toFocus === 'first'
                    ? 0
                    : props.parent.url
                        ? props.links.length
                        : props.links.length - 1]?.focus();
            }, 150);
        }
    }, [props.toFocus]);
    return (React.createElement(S.Dropdown, { role: "none", "data-relative": !props.hasChildren, "data-active": props.active, onMouseLeave: onMouseLeave },
        React.createElement(S.Level, { role: "menu", "aria-label": props.parent.text, "data-active": props.active },
            React.createElement(S.BackButton, { onClick: () => props.goBack(false), onKeyDown: (e) => handleBackButtonKeypress(e, props.goBack) },
                getSvgIcon(InterfaceIcon.ArrowRight),
                props.parent.text),
            props.parent.url && (React.createElement("li", { role: "none" },
                React.createElement(S.SubLink, { ref: (el) => (itemsRef.current[0] = el), href: props.parent.url, target: props.parent.target, role: "menuitem", "data-parent": true, onKeyDown: (event) => handleSubLinkKeypress(event, 0, props.links.length, itemsRef, false, setActive, setFocus, props.goBack), onMouseEnter: () => setActive(-1) },
                    React.createElement(S.SubLinkTitle, null,
                        props.parent.text,
                        " ",
                        React.createElement(FakeButton, { buttonType: "primary", iconOnly: true })),
                    props.parent.description && (React.createElement(S.SubLinkDescription, null, props.parent.description))))),
            props.links.map((link, i) => (React.createElement("li", { key: link.id, role: "none" },
                React.createElement(S.SubLink, { role: "menuitem", ref: (el) => (itemsRef.current[props.parent.url ? i + 1 : i] =
                        el), href: link.url, target: link.target, ...(link.children && link.children.length > 0
                        ? {
                            'aria-haspopup': 'menu',
                            'aria-expanded': active === (props.parent.url ? i + 1 : i),
                            'data-active': active === (props.parent.url ? i + 1 : i),
                            onClick: (e) => {
                                if (!props.isDesktop) {
                                    e.preventDefault();
                                    setActive(props.parent.url ? i + 1 : i);
                                }
                            },
                        }
                        : {}), onKeyDown: (event) => handleSubLinkKeypress(event, props.parent.url ? i + 1 : i, props.parent.url ? props.links.length : props.links.length - 1, itemsRef, !!link.children && link.children.length > 0, setActive, setFocus, props.goBack), onMouseEnter: () => {
                        if (props.isDesktop) {
                            setActive(props.parent.url ? i + 1 : i);
                        }
                    }, onFocus: () => {
                        if (props.isDesktop) {
                            setActive(props.parent.url ? i + 1 : i);
                        }
                    } },
                    React.createElement(S.SubLinkTitle, null,
                        link.text,
                        (!link.children || link.children.length === 0) && (React.createElement(React.Fragment, null, getSvgIcon(InterfaceIcon.ArrowRight)))),
                    link.description && React.createElement(S.SubLinkDescription, null, link.description)))))),
        props.links.map((link, i) => (React.createElement(React.Fragment, { key: `${link.id}-column` }, link.children && link.children.length > 0 && (React.createElement(NavigationColumns, { active: props.active && active === (props.parent.url ? i + 1 : i), ariaLabel: link.text, goBack: (doFocus) => {
                setActive(-1);
                if (doFocus) {
                    itemsRef.current[props.parent.url ? i + 1 : i]?.focus();
                }
            }, items: link.children, level: 1, toFocus: focus, removeFocus: () => setFocus(undefined), parent: link, isDesktop: props.isDesktop })))))));
};
const NavigationColumns = (props) => {
    const [active, setActive] = React.useState(-1);
    const [focus, setFocus] = React.useState();
    const itemsRef = React.useRef([]);
    const singleLevel = React.useMemo(() => props.level === 1 &&
        (props.items[0].type === 'nav-card' ||
            (props.items[0].type === 'nav-link' &&
                (!props.items[0].children || props.items[0].children.length === 0) &&
                props.items.length > 6)), []);
    React.useEffect(() => {
        if (!props.active) {
            setActive(-1);
            setFocus(undefined);
            props.removeFocus();
        }
    }, [props.active]);
    React.useEffect(() => {
        if (props.active && props.toFocus) {
            setTimeout(() => {
                itemsRef.current[props.toFocus === 'first' ? 0 : props.items.length - 1]?.focus();
            }, 150);
        }
    }, [props.toFocus]);
    return (React.createElement(React.Fragment, null,
        React.createElement(S.Level, { role: "menu", "aria-label": props.ariaLabel, "data-full": singleLevel, "data-level": props.level, "data-active": props.active },
            React.createElement(S.BackButton, { onClick: () => props.goBack(false), onKeyDown: (e) => handleBackButtonKeypress(e, props.goBack) },
                getSvgIcon(InterfaceIcon.ArrowRight),
                props.ariaLabel),
            props.parent.url && (React.createElement("li", { "data-mobile": true },
                React.createElement(S.SubLink, { href: props.parent.url, target: props.parent.target, onMouseEnter: () => {
                        if (props.isDesktop) {
                            setActive(-1);
                        }
                    } },
                    React.createElement(S.SubLinkTitle, null,
                        props.parent.text,
                        getSvgIcon(InterfaceIcon.ArrowRight))))),
            props.items.map((link, i) => (React.createElement("li", { key: link.id, role: "none" }, link.type === 'nav-link' ? (React.createElement(S.SubLink, { ref: (el) => (itemsRef.current[i] = el), href: link.url, target: link.target, role: "menuitem", ...(link.children && link.children.length > 0
                    ? {
                        'aria-haspopup': 'menu',
                        'aria-expanded': active === i,
                        'data-active': active === i,
                        onClick: (e) => {
                            if (!props.isDesktop) {
                                e.preventDefault();
                                setActive(i);
                            }
                        },
                    }
                    : {}), onKeyDown: (event) => handleSubLinkKeypress(event, i, props.items.length - 1, itemsRef, !!link.children && link.children.length > 0, setActive, setFocus, props.goBack), onMouseEnter: () => {
                    if (props.isDesktop) {
                        setActive(i);
                    }
                }, onFocus: () => {
                    if (props.isDesktop) {
                        setActive(i);
                    }
                } },
                React.createElement(S.SubLinkTitle, null,
                    link.icon && link.icon.type && (React.createElement(S.SubLinkIcon, { "data-colour": link.icon.colour }, getSvgIcon(link.icon.type))),
                    link.text,
                    (!link.children || link.children.length === 0) && (React.createElement(React.Fragment, null, getSvgIcon(InterfaceIcon.ArrowRight)))))) : (React.createElement(S.SubLink, { ref: (el) => (itemsRef.current[i] = el), href: link.url, target: link.target, "data-card": true, onKeyDown: (event) => handleSubLinkKeypress(event, i, props.items.length - 1, itemsRef, false, setActive, setFocus, props.goBack) },
                link.image && (React.createElement(S.SubLinkImage, null,
                    React.createElement(S.Image, { src: imageUrl(link.image, { width: 104, height: 204 }), alt: link.image.altText, loading: "lazy" }))),
                React.createElement(S.SubLinkTitle, null,
                    link.text,
                    React.createElement(React.Fragment, null, getSvgIcon(InterfaceIcon.ArrowRight))),
                link.description && (React.createElement(S.SubLinkDescription, null, link.description)))))))),
        props.items.map((link, i) => (React.createElement(React.Fragment, { key: `${link.id}-column` }, link.type !== 'nav-card' && link.children && link.children.length > 0 && (React.createElement(NavigationColumns, { active: active === i, ariaLabel: link.text, goBack: (doFocus) => {
                setActive(-1);
                if (doFocus) {
                    itemsRef.current[i]?.focus();
                }
            }, items: link.children, level: props.level + 1, toFocus: focus, removeFocus: () => setFocus(undefined), parent: link, isDesktop: props.isDesktop })))))));
};
function handleSubLinkKeypress(event, i, maxIndex, itemsRef, isButton, setActive, setFocus, goBack) {
    const nextIndex = i === maxIndex ? 0 : clamp(i + 1, maxIndex);
    const prevIndex = i === 0 ? maxIndex : clamp(i - 1, maxIndex);
    switch (event.key) {
        case 'Space':
        case 'Enter':
        case 'ArrowRight':
            if (isButton) {
                event.preventDefault();
                event.stopPropagation();
                setActive(i);
                setFocus('first');
            }
            break;
        case 'ArrowDown':
            event.preventDefault();
            event.stopPropagation();
            itemsRef.current[nextIndex]?.focus();
            break;
        case 'ArrowUp':
            event.preventDefault();
            event.stopPropagation();
            itemsRef.current[prevIndex]?.focus();
            break;
        case 'Home':
            event.preventDefault();
            event.stopPropagation();
            itemsRef.current[0]?.focus();
            break;
        case 'End':
            event.preventDefault();
            event.stopPropagation();
            itemsRef.current[maxIndex]?.focus();
            break;
        case 'ArrowLeft':
        case 'Escape':
            event.preventDefault();
            event.stopPropagation();
            goBack(true);
            break;
        default:
            break;
    }
}
function handleBackButtonKeypress(event, goBack) {
    switch (event.key) {
        case 'Space':
        case 'Enter':
        case 'ArrowLeft':
        case 'Escape':
            event.preventDefault();
            event.stopPropagation();
            goBack(true);
            break;
        default:
            break;
    }
}
export default Navigation;
